<template>
  <div>
    <template v-for="(value, key) in modelValue" :key="key">

      <!-- Boolean (top-level) -->
      <div v-if="typeof value === 'boolean'" class="mb-3 d-flex align-items-center gap-3">
        <label class="text-white text-capitalize mb-0" style="min-width:140px">{{ formatKey(key) }}</label>
        <div class="cfe-toggle" :class="{ active: value }" @click="setField(key, !value)">
          <div class="cfe-knob"></div>
        </div>
        <span class="text-silver" style="font-size:0.85rem">{{ value ? 'Yes' : 'No' }}</span>
      </div>

      <!-- Image field (top-level string) -->
      <div v-else-if="typeof value === 'string' && isImageField(String(key))" class="mb-3">
        <label class="text-white text-capitalize d-block mb-1">{{ formatKey(key) }}</label>
        <div class="d-flex gap-2 align-items-center">
          <input :value="value"
            @input="setField(key, ($event.target as HTMLInputElement).value)"
            type="text" class="form-control bg-dark text-white border-secondary"
            placeholder="Paste URL or upload →" />
          <label class="btn btn-sm mb-0" style="cursor:pointer;white-space:nowrap">
            <span class="border-style">Upload</span>
            <input type="file" class="d-none" accept="image/*"
              @change="uploadField($event, (url) => setField(key, url))" />
          </label>
        </div>
        <img v-if="value" :src="value" class="mt-2 rounded"
          style="max-height:100px;max-width:220px;object-fit:cover;display:block" />
      </div>

      <!-- Array of objects -->
      <div v-else-if="isObjArray(value)" class="mb-4">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <label class="text-white fw-bold text-capitalize">{{ formatKey(key) }}</label>
          <button type="button" class="btn btn-sm" @click="addItem(key as string)">
            <span class="border-style">+ Add</span>
          </button>
        </div>
        <div v-for="(item, idx) in (value as any[])" :key="idx"
          class="border border-secondary rounded p-3 mb-3 position-relative">
          <button type="button" class="cfe-remove-btn"
            @click="removeItem(key as string, idx)">✕</button>

          <div v-for="(fv, fk) in item" :key="fk" class="mb-2">
            <label class="text-silver small d-block mb-1 text-capitalize">{{ formatKey(fk) }}</label>

            <!-- Boolean in object -->
            <div v-if="typeof fv === 'boolean'" class="d-flex align-items-center gap-2">
              <div class="cfe-toggle" :class="{ active: fv }"
                @click="setNested(key as string, idx, fk as string, !fv)">
                <div class="cfe-knob"></div>
              </div>
              <span class="text-silver" style="font-size:0.85rem">{{ fv ? 'Yes' : 'No' }}</span>
            </div>

            <!-- Image field in object -->
            <div v-else-if="isImageField(String(fk))">
              <div class="d-flex gap-2 align-items-center">
                <input :value="fv"
                  @input="setNested(key as string, idx, fk as string, ($event.target as HTMLInputElement).value)"
                  type="text" class="form-control form-control-sm bg-dark text-white border-secondary"
                  placeholder="Paste URL or upload →" />
                <label class="btn btn-sm mb-0" style="cursor:pointer;white-space:nowrap">
                  <span class="border-style">Upload</span>
                  <input type="file" class="d-none" accept="image/*"
                    @change="uploadField($event, (url) => setNested(key as string, idx, fk as string, url))" />
                </label>
              </div>
              <img v-if="fv" :src="fv" class="mt-1 rounded"
                style="max-height:80px;max-width:180px;object-fit:cover;display:block" />
            </div>

            <!-- Nested array of objects (2nd level, e.g. footer.service_links inside items) -->
            <div v-else-if="isObjArray(fv)">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <span class="text-silver" style="font-size:0.8rem">{{ (fv as any[]).length }} item(s)</span>
                <button type="button" class="cfe-mini-btn"
                  @click="addSubItem(key as string, idx, fk as string, fv as any[])">+ Add</button>
              </div>
              <div v-for="(sv, si) in (fv as any[])" :key="si"
                class="d-flex gap-1 align-items-center mb-1 flex-wrap">
                <template v-for="(svv, svk) in sv" :key="svk">
                  <div v-if="typeof svv === 'boolean'" class="d-flex align-items-center gap-1">
                    <span class="text-silver" style="font-size:0.75rem">{{ formatKey(svk) }}</span>
                    <div class="cfe-toggle cfe-toggle-sm" :class="{ active: svv }"
                      @click="setDeep(key as string, idx, fk as string, si, svk as string, !svv)">
                      <div class="cfe-knob"></div>
                    </div>
                  </div>
                  <input v-else :value="svv"
                    @input="setDeep(key as string, idx, fk as string, si, svk as string, ($event.target as HTMLInputElement).value)"
                    type="text" class="form-control form-control-sm bg-dark text-white border-secondary"
                    :placeholder="String(svk)" style="flex:1;min-width:80px" />
                </template>
                <button type="button" class="cfe-mini-btn cfe-mini-danger"
                  @click="removeSubItem(key as string, idx, fk as string, si)">✕</button>
              </div>
            </div>

            <!-- Nested array of strings -->
            <div v-else-if="Array.isArray(fv) && !isObjArray(fv)" class="d-flex flex-column gap-1">
              <div v-for="(sv, si) in (fv as string[])" :key="si" class="d-flex gap-1">
                <input :value="sv"
                  @input="setNestedStr(key as string, idx, fk as string, si, ($event.target as HTMLInputElement).value)"
                  type="text"
                  class="form-control form-control-sm bg-dark text-white border-secondary flex-grow-1" />
                <button class="cfe-mini-btn cfe-mini-danger"
                  @click="removeNestedStr(key as string, idx, fk as string, si)">✕</button>
              </div>
            </div>

            <!-- Textarea in object (long string) -->
            <textarea v-else-if="typeof fv === 'string' && fv.length > 80"
              :value="fv"
              @input="setNested(key as string, idx, fk as string, ($event.target as HTMLTextAreaElement).value)"
              rows="3"
              class="form-control form-control-sm bg-dark text-white border-secondary"></textarea>

            <!-- Number in object -->
            <input v-else-if="typeof fv === 'number'"
              :value="fv"
              @input="setNested(key as string, idx, fk as string, Number(($event.target as HTMLInputElement).value))"
              type="number"
              class="form-control form-control-sm bg-dark text-white border-secondary" />

            <!-- String in object (default) -->
            <input v-else
              :value="fv"
              @input="setNested(key as string, idx, fk as string, ($event.target as HTMLInputElement).value)"
              type="text"
              class="form-control form-control-sm bg-dark text-white border-secondary" />
          </div>
        </div>
      </div>

      <!-- Array of primitives -->
      <div v-else-if="Array.isArray(value)" class="mb-4">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <label class="text-white fw-bold text-capitalize">{{ formatKey(key) }}</label>
          <button type="button" class="btn btn-sm" @click="addStringItem(key as string)">
            <span class="border-style">+ Add</span>
          </button>
        </div>
        <div v-for="(item, idx) in (value as any[])" :key="idx" class="d-flex gap-2 mb-2">
          <input :value="item"
            @input="setStringArray(key as string, idx, ($event.target as HTMLInputElement).value)"
            type="text"
            class="form-control form-control-sm bg-dark text-white border-secondary flex-grow-1" />
          <button type="button" class="btn btn-sm" @click="removeItem(key as string, idx)">
            <span class="border-style">✕</span>
          </button>
        </div>
      </div>

      <!-- Number (top-level) -->
      <div v-else-if="typeof value === 'number'" class="mb-3">
        <label class="text-white text-capitalize d-block mb-1">{{ formatKey(key) }}</label>
        <input :value="value"
          @input="setField(key, Number(($event.target as HTMLInputElement).value))"
          type="number" class="form-control bg-dark text-white border-secondary" />
      </div>

      <!-- Long string (top-level) -->
      <div v-else-if="typeof value === 'string' && value.length > 80" class="mb-3">
        <label class="text-white text-capitalize d-block mb-1">{{ formatKey(key) }}</label>
        <textarea :value="value"
          @input="setField(key, ($event.target as HTMLTextAreaElement).value)"
          rows="3" class="form-control bg-dark text-white border-secondary"></textarea>
      </div>

      <!-- Short string (top-level) -->
      <div v-else-if="typeof value === 'string'" class="mb-3">
        <label class="text-white text-capitalize d-block mb-1">{{ formatKey(key) }}</label>
        <input :value="value"
          @input="setField(key, ($event.target as HTMLInputElement).value)"
          type="text" class="form-control bg-dark text-white border-secondary" />
      </div>

    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import api from "@/services/api";

export default defineComponent({
  name: "ContentFieldEditor",
  props: {
    modelValue: { type: Object as () => Record<string, any>, required: true },
  },
  emits: ["update:modelValue"],

  setup(props, { emit }) {
    function formatKey(key: string | number | symbol) {
      return String(key).replace(/_/g, " ");
    }

    function isImageField(key: string) {
      const k = key.toLowerCase();
      return k.includes("image") || k.includes("logo") || k.includes("photo") ||
             k.includes("thumbnail") || k.includes("avatar") || k.includes("picture") ||
             k.endsWith("_img") || (k.endsWith("_url") && (k.includes("logo") || k.includes("image") || k.includes("photo") || k.includes("banner") || k.includes("avatar")));
    }

    function isObjArray(val: unknown): val is Record<string, any>[] {
      return Array.isArray(val) && val.length > 0 && typeof val[0] === "object" && val[0] !== null;
    }

    // ── Emit helpers ────────────────────────────────────────────────────────────
    function setField(key: string | number | symbol, val: any) {
      emit("update:modelValue", { ...props.modelValue, [String(key)]: val });
    }

    function setNested(key: string, idx: number, field: string, val: any) {
      const arr = [...(props.modelValue[key] as any[])];
      arr[idx] = { ...arr[idx], [field]: val };
      setField(key, arr);
    }

    function setDeep(key: string, idx: number, field: string, si: number, sk: string, val: any) {
      const arr = [...(props.modelValue[key] as any[])];
      const sub = [...(arr[idx][field] as any[])];
      sub[si] = { ...sub[si], [sk]: val };
      arr[idx] = { ...arr[idx], [field]: sub };
      setField(key, arr);
    }

    function setStringArray(key: string, idx: number, val: string) {
      const arr = [...(props.modelValue[key] as string[])];
      arr[idx] = val;
      setField(key, arr);
    }

    function setNestedStr(key: string, idx: number, field: string, si: number, val: string) {
      const arr = [...(props.modelValue[key] as any[])];
      const sub = [...(arr[idx][field] as string[])];
      sub[si] = val;
      arr[idx] = { ...arr[idx], [field]: sub };
      setField(key, arr);
    }

    // ── Add / remove ────────────────────────────────────────────────────────────
    function addItem(key: string) {
      const arr = props.modelValue[key] as any[];
      const template = arr.length > 0
        ? Object.fromEntries(Object.entries(arr[0]).map(([k, v]) => [k, typeof v === "boolean" ? false : typeof v === "number" ? 0 : ""]))
        : {};
      setField(key, [...arr, template]);
    }

    function removeItem(key: string, idx: number) {
      const arr = [...(props.modelValue[key] as any[])];
      arr.splice(idx, 1);
      setField(key, arr);
    }

    function addStringItem(key: string) {
      setField(key, [...(props.modelValue[key] as any[]), ""]);
    }

    function addSubItem(key: string, idx: number, field: string, sub: any[]) {
      const template = sub.length > 0
        ? Object.fromEntries(Object.entries(sub[0]).map(([k, v]) => [k, typeof v === "boolean" ? false : typeof v === "number" ? 0 : ""]))
        : {};
      setDeep(key, idx, field, sub.length, Object.keys(template)[0] ?? "item", template);
      // replace entire sub-array
      const arr = [...(props.modelValue[key] as any[])];
      arr[idx] = { ...arr[idx], [field]: [...sub, template] };
      setField(key, arr);
    }

    function removeSubItem(key: string, idx: number, field: string, si: number) {
      const arr = [...(props.modelValue[key] as any[])];
      const sub = [...(arr[idx][field] as any[])];
      sub.splice(si, 1);
      arr[idx] = { ...arr[idx], [field]: sub };
      setField(key, arr);
    }

    function removeNestedStr(key: string, idx: number, field: string, si: number) {
      const arr = [...(props.modelValue[key] as any[])];
      const sub = [...(arr[idx][field] as string[])];
      sub.splice(si, 1);
      arr[idx] = { ...arr[idx], [field]: sub };
      setField(key, arr);
    }

    // ── Image upload ─────────────────────────────────────────────────────────────
    async function uploadField(event: Event, onSuccess: (url: string) => void) {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file) return;
      const form = new FormData();
      form.append("file", file);
      try {
        const { data } = await api.post("/api/admin/media/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        onSuccess(data.url);
      } catch {
        alert("Upload failed. Max 5 MB, images only.");
      } finally {
        (event.target as HTMLInputElement).value = "";
      }
    }

    return {
      formatKey, isImageField, isObjArray,
      setField, setNested, setDeep, setStringArray, setNestedStr,
      addItem, removeItem, addStringItem, addSubItem, removeSubItem, removeNestedStr,
      uploadField,
    };
  },
});
</script>

<style scoped>
/* Toggle switch */
.cfe-toggle {
  width: 44px; height: 24px;
  background: #3a2526; border-radius: 12px;
  position: relative; cursor: pointer;
  transition: background 0.2s; flex-shrink: 0;
}
.cfe-toggle.active { background: #198754; }
.cfe-knob {
  width: 18px; height: 18px;
  background: #fff; border-radius: 50%;
  position: absolute; top: 3px; left: 3px;
  transition: left 0.2s;
}
.cfe-toggle.active .cfe-knob { left: 23px; }
.cfe-toggle-sm { width: 34px; height: 20px; border-radius: 10px; }
.cfe-toggle-sm .cfe-knob { width: 14px; height: 14px; top: 3px; left: 3px; }
.cfe-toggle-sm.active .cfe-knob { left: 17px; }

/* Remove button on object cards */
.cfe-remove-btn {
  position: absolute; top: 0.5rem; right: 0.5rem;
  background: transparent; border: 1px solid #6c757d;
  color: #6c757d; border-radius: 4px;
  padding: 0.15rem 0.45rem; cursor: pointer; font-size: 0.75rem;
  line-height: 1.4;
}
.cfe-remove-btn:hover { border-color: #dc3545; color: #f87171; }

/* Mini inline buttons */
.cfe-mini-btn {
  background: transparent; border: 1px solid #6c757d;
  color: #c0c0c0; border-radius: 4px;
  padding: 0.15rem 0.5rem; cursor: pointer; font-size: 0.78rem;
  white-space: nowrap; transition: all 0.15s;
}
.cfe-mini-btn:hover { border-color: #c90e0e; color: #c90e0e; }
.cfe-mini-danger { border-color: #4a3030; }
.cfe-mini-danger:hover { background: rgba(220,53,69,0.1); border-color: #dc3545; color: #f87171; }
</style>
